import { styled, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { Formik } from 'formik'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { UserDataForPermit } from '../../../../__generated__/api'
import {
  DefaultButton,
  DefaultLoadingButton,
} from '../../../../common/components/DefaultButton/DefaultButton'
import DatePickerNew from '../../../../common/components/InputFieldNew/DatePickerNew'
import { ColumnFlex } from '../../../../common/components/Layout/Layout'
import ScrollToError from '../../../../common/components/ScrollToError/ScrollToError'
import { Text } from '../../../../common/components/Typography/Typography'
import { scale } from '../../../../common/utils/scale'
import { AccidentDetailsFormData } from '../../utils/insurance'

import { ButtonsContainer, ContentContainer, Heading } from './Shared'

export type FormValues = {
  incidentDate: Date | undefined
  companyName: string
}

interface Props {
  translationKey: string
  userDataForPermit: UserDataForPermit[] | undefined
  onSubmit: (data: AccidentDetailsFormData[]) => void
  onBack: () => void
}

const StyledForm = styled('form')`
  width: 100%;
  max-width: 560px;

  /* Fix for MUI label text wrapping */
  .MuiFormLabel-root,
  .MuiInputLabel-root {
    // white-space: normal forces the text to wrap naturally instead of being truncated with an ellipsis (...)
    white-space: normal;
  }
`

const Container = styled(ContentContainer)`
  padding: ${scale(2)};
`

// Form fields container with consistent gap spacing
const FormFieldsContainer = styled(ColumnFlex)`
  gap: ${scale(2)};
`

const PohjolaCompanyDetailsForm: React.FC<React.PropsWithChildren<Props>> = ({
  translationKey,
  onSubmit,
  onBack,
  userDataForPermit,
}) => {
  const { t } = useTranslation()

  // Check which fields are present in userDataForPermit
  const fieldsPresent = useMemo(() => {
    const presentMap: Record<string, boolean> = {
      incidentDate: false,
      companyName: false,
    }

    userDataForPermit?.forEach((item) => {
      if (item.id === 'incidentDate') {
        presentMap.incidentDate = true
      } else if (item.id === 'employer') {
        presentMap.companyName = true
      }
    })

    return presentMap
  }, [userDataForPermit])

  // Create a map of which fields are required based on userDataForPermit
  const requiredFields = useMemo(() => {
    const requiredMap: Record<string, boolean> = {
      incidentDate: false,
      companyName: false,
    }

    userDataForPermit?.forEach((item) => {
      if (item.id === 'incidentDate') {
        requiredMap.incidentDate = item.required
      } else if (item.id === 'employer') {
        requiredMap.companyName = item.required
      }
    })

    return requiredMap
  }, [userDataForPermit])

  // Dynamically build validation schema based on required fields
  const validationSchema = useMemo(() => {
    const schema: any = {}

    if (fieldsPresent.incidentDate) {
      let incidentDateSchema = Yup.date()
      if (requiredFields.incidentDate) {
        incidentDateSchema = incidentDateSchema.required(t('validation.required'))
      }
      schema.incidentDate = incidentDateSchema
    }

    if (fieldsPresent.companyName) {
      let companyNameSchema = Yup.string().max(
        40,
        t('validation.string.max', {
          max: 40,
        })
      )
      if (requiredFields.companyName) {
        companyNameSchema = companyNameSchema.required(t('validation.required'))
      }
      schema.companyName = companyNameSchema
    }

    return Yup.object(schema)
  }, [fieldsPresent, requiredFields, t])

  const initialValues: FormValues = {
    incidentDate: undefined,
    companyName: '',
  }

  const handleFormSubmit = (values: FormValues) => {
    // Transform the form values to match the userDataForPermit structure with values
    const userDataWithValues =
      userDataForPermit?.map((item) => {
        let value = ''

        if (item.id === 'incidentDate' && values.incidentDate) {
          value = dayjs(values.incidentDate).format('YYYY-MM-DD')
        } else if (item.id === 'employer') {
          value = values.companyName || ''
        }

        return {
          id: item.id,
          required: item.required,
          value,
        }
      }) || []

    onSubmit(userDataWithValues)
  }

  const FormInfoText = styled(Text)`
    display: block;
    margin-bottom: 20px;
    margin-top: 20px;
  `

  return (
    <Container>
      <Heading $size={500} $weight="Medium" $height="Medium">
        {t(`component.${translationKey}.accidentDetailsForm.title`)}
      </Heading>

      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={handleFormSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, handleSubmit }) => (
          <StyledForm onSubmit={handleSubmit}>
            <ScrollToError />
            <FormInfoText $size={300}>
              {t('component.reserveContactInformationForm.requiredFieldsInstruction')}
            </FormInfoText>

            <FormFieldsContainer>
              {fieldsPresent.incidentDate && (
                <DatePickerNew
                  id="incidentDate"
                  label={t(`component.${translationKey}.accidentDetailsForm.incidentDate`)}
                  helperText={
                    touched.incidentDate && errors.incidentDate
                      ? String(errors.incidentDate)
                      : undefined
                  }
                  error={Boolean(touched.incidentDate && errors.incidentDate)}
                  value={values.incidentDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required={requiredFields.incidentDate}
                  max={dayjs().format('YYYY-MM-DD')}
                />
              )}

              {fieldsPresent.companyName && (
                <TextField
                  id="companyName"
                  label={t(`component.${translationKey}.accidentDetailsForm.companyName`)}
                  placeholder={t(
                    `component.${translationKey}.accidentDetailsForm.companyNamePlaceholder`
                  )}
                  helperText={touched.companyName && errors.companyName}
                  error={Boolean(touched.companyName && errors.companyName)}
                  value={values.companyName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required={requiredFields.companyName}
                  fullWidth
                />
              )}
            </FormFieldsContainer>

            <ButtonsContainer>
              <DefaultLoadingButton type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {t('common.continue')}
              </DefaultLoadingButton>
              <DefaultButton type="button" variant="text" onClick={onBack}>
                {t('common.goBack')}
              </DefaultButton>
            </ButtonsContainer>
          </StyledForm>
        )}
      </Formik>
    </Container>
  )
}

export default PohjolaCompanyDetailsForm
